import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { SUPPORTED_FRAMEWORKS } from "../constants/frameworks";

const OrgContext = createContext(null);
export const useOrg = () => useContext(OrgContext);

// Reference frameworks are always-on cross-mapping targets (ADR-S17b — §4.3).
// They must always be present in activeFrameworks regardless of org selection
// so that Mappings.jsx and cross-mapping targets continue to work.
const INTL_REFERENCE_IDS = ["nist_csf_2", "iso_31000", "iso_22301"];

export const OrgProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOrg = useCallback(async () => {
    if (!token || !user?.organization_id) {
      setOrg(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/organizations/current`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrg(res.data);
    } catch (e) {
      console.error("org fetch failed:", e?.response?.status);
      setOrg(null);
    } finally {
      setLoading(false);
    }
  }, [token, user?.organization_id]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  const activeFrameworks = useMemo(() => {
    // Backfill: empty or absent active_frameworks → show all (ADR-S17b-001).
    const stored = Array.isArray(org?.active_frameworks) && org.active_frameworks.length > 0
      ? org.active_frameworks
      : SUPPORTED_FRAMEWORKS;

    // S17b simplification: no longer auto-merge RD_LATAM or ZF frameworks.
    // The org admin explicitly selects which frameworks are active via the
    // Framework Picker (/settings/frameworks). What they stored is what they see.
    //
    // Exception: reference frameworks (NIST CSF 2.0, ISO 31000, ISO 22301) are
    // always injected because they serve as cross-mapping targets (§4.3 diseño S17b).
    const merged = [...stored];
    for (const id of INTL_REFERENCE_IDS) {
      if (!merged.includes(id)) merged.push(id);
    }
    return merged;
  }, [org?.active_frameworks]);

  const hasFramework = useCallback(
    (id) => activeFrameworks.includes(id),
    [activeFrameworks]
  );

  const filterFrameworks = useCallback(
    (list, getId = (x) => x.id || x.key || x) =>
      list.filter((item) => activeFrameworks.includes(getId(item))),
    [activeFrameworks]
  );

  return (
    <OrgContext.Provider
      value={{ org, loading, activeFrameworks, hasFramework, filterFrameworks, refresh: fetchOrg }}
    >
      {children}
    </OrgContext.Provider>
  );
};
